import { Box, Button, Chip, TextField, Typography } from "@mui/material";
import { Theme, alpha } from "@mui/material/styles";
import React, { useEffect, useState } from "react";

import { toCountryCode, toTrustedSet } from "../../ValueChain/valueChainState";

// How many countries of the flow are offered as quick picks: the row stays
// one row, and the field takes any other code
const QUICK_PICKS = 8;

// A field of the row: as tall as its chips' row allows, its error under it
// without pushing the row apart
const FIELD_SX = {
  "& .MuiInputBase-root": { height: 32, fontSize: 13 },
  "& .MuiFormHelperText-root": {
    position: "absolute",
    top: "100%",
    left: 0,
    m: 0,
    mt: 0.25,
    whiteSpace: "nowrap",
  },
} as const;

const BUTTON_SX = { height: 32, minWidth: 0, px: 1.5 } as const;

// The small uppercase word before a control
const Lbl: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography
    component="span"
    sx={{
      fontSize: 11,
      fontWeight: 500,
      color: "text.secondary",
      textTransform: "uppercase",
      letterSpacing: "0.06em",
    }}
  >
    {children}
  </Typography>
);

interface SupplyFlowControlsProps {
  // The home jurisdiction in force: the address's, else the remembered one
  home: string | null;
  onHomeChange: (home: string | null) => void;
  trusted: string[];
  onTrustedChange: (trusted: string[]) => void;
  // The country codes of the flow, offered as quick picks for the home
  countries: string[];
  // What a view adds at the far end of the row
  children?: React.ReactNode;
}

// The jurisdiction controls of the supply flow, as one row: the home
// jurisdiction at the near end, the trusted set at the far end. They are the
// pieces of the value chain's settings (same codes, same words) without its
// flag rule, which this view has no use for
const SupplyFlowControls: React.FC<SupplyFlowControlsProps> = ({
  home,
  onHomeChange,
  trusted,
  onTrustedChange,
  countries,
  children,
}) => {
  const [homeDraft, setHomeDraft] = useState(home ?? "");
  const [trustedDraft, setTrustedDraft] = useState("");

  // The field follows the address when it changes elsewhere
  useEffect(() => {
    setHomeDraft(home ?? "");
  }, [home]);

  const homeCode = toCountryCode(homeDraft);
  const homeInvalid = homeDraft.trim() !== "" && homeCode === null;
  const trustedCode = toCountryCode(trustedDraft);

  const submitHome = () => {
    if (homeDraft.trim() === "") {
      if (home !== null) {
        onHomeChange(null);
      }
      return;
    }
    if (homeCode && homeCode !== home) {
      onHomeChange(homeCode);
    }
  };

  const addTrusted = () => {
    if (!trustedCode) {
      return;
    }
    onTrustedChange(toTrustedSet([...trusted, trustedCode]));
    setTrustedDraft("");
  };

  return (
    <Box
      role="group"
      aria-label="Jurisdiction"
      sx={{ display: "flex", columnGap: 1, rowGap: 1, alignItems: "center", flexWrap: "wrap" }}
    >
      <Lbl>Home</Lbl>
      <Box
        component="form"
        onSubmit={(event: React.FormEvent) => {
          event.preventDefault();
          submitHome();
        }}
        sx={{ display: "flex", gap: 0.75, alignItems: "center", position: "relative" }}
      >
        <TextField
          size="small"
          placeholder="NO"
          value={homeDraft}
          onChange={(event) => setHomeDraft(event.target.value)}
          error={homeInvalid}
          helperText={homeInvalid ? "A country code: two or three letters" : undefined}
          inputProps={{
            "aria-label": "Home jurisdiction",
            maxLength: 3,
            style: { textTransform: "uppercase" },
          }}
          sx={{ ...FIELD_SX, width: 64 }}
        />
        <Button type="submit" size="small" variant="outlined" sx={BUTTON_SX}>
          Set
        </Button>
      </Box>

      {countries.length > 0 && (
        <Box
          role="group"
          aria-label="Countries in this flow"
          sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}
        >
          {countries.slice(0, QUICK_PICKS).map((code) => (
            <Chip
              key={code}
              label={code}
              size="small"
              aria-pressed={code === home}
              aria-label={`Set ${code} as the home jurisdiction`}
              onClick={() => onHomeChange(code)}
              sx={
                code === home
                  ? (theme: Theme) => ({
                      // The chip of the home in force, in the home's colour
                      backgroundColor: alpha(theme.palette.chart.home, 0.14),
                      borderColor: alpha(theme.palette.chart.home, 0.45),
                    })
                  : undefined
              }
            />
          ))}
        </Box>
      )}

      <Box
        sx={{
          ml: "auto",
          display: "flex",
          columnGap: 1,
          rowGap: 1,
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <Lbl>Trusted</Lbl>
        <Box
          role="list"
          aria-label="Trusted countries"
          sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}
        >
          {trusted.length === 0 ? (
            <Typography variant="caption" color="text.disabled">
              None
            </Typography>
          ) : (
            trusted.map((code) => (
              <Chip
                key={code}
                role="listitem"
                label={code}
                size="small"
                onDelete={() =>
                  onTrustedChange(trusted.filter((value) => value !== code))
                }
                deleteIcon={<span aria-label={`Remove ${code}`}>×</span>}
              />
            ))
          )}
        </Box>
        <Box
          component="form"
          onSubmit={(event: React.FormEvent) => {
            event.preventDefault();
            addTrusted();
          }}
          sx={{ display: "flex", gap: 0.75, alignItems: "center" }}
        >
          <TextField
            size="small"
            placeholder="SE"
            value={trustedDraft}
            onChange={(event) => setTrustedDraft(event.target.value)}
            inputProps={{
              "aria-label": "Add a trusted country",
              maxLength: 3,
              style: { textTransform: "uppercase" },
            }}
            sx={{ ...FIELD_SX, width: 64 }}
          />
          <Button
            type="submit"
            size="small"
            variant="outlined"
            disabled={!trustedCode}
            sx={BUTTON_SX}
          >
            Add
          </Button>
        </Box>
        {children}
      </Box>
    </Box>
  );
};

export default SupplyFlowControls;
